"use client";

import { c as _c } from "react/compiler-runtime";
import { Button } from "@/components/rac/button";
import { Card } from "@/components/rac/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/radix/avatar";
import { GET_REVIEWS_QUERY, ReviewData } from "@/gql/review";
import toStr from "@/utils/date/format";
import { logger } from "@/utils/logger";
import { useLazyQuery } from "@apollo/client";
import { Loader2, Shuffle, Star } from "lucide-react";
import { useCallback } from "react";
const ITEMS_PER_PAGE = 12;
interface ReviewsClientProps {
  initialReviews: ReviewData[];
}
export function ReviewsClient(t0) {
  const $ = _c(16);
  const {
    initialReviews
  } = t0;
  const [getReviews, t1] = useLazyQuery(GET_REVIEWS_QUERY);
  const {
    data,
    loading
  } = t1;
  let t2;
  if ($[0] !== getReviews || $[1] !== loading) {
    t2 = () => {
      if (loading) {
        return;
      }
      getReviews({
        variables: {
          params: {
            get: {
              limit: ITEMS_PER_PAGE
            }
          }
        }
      });
    };
    $[0] = getReviews;
    $[1] = loading;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const fetchRandomReviews = t2;
  const reviews = data?.reviews?.get?.data || initialReviews;
  let t3;
  if ($[3] !== reviews) {
    t3 = reviews.map(_temp);
    $[3] = reviews;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t3) {
    t4 = <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">{t3}</div>;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== loading) {
    t5 = loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Loading Random Reviews...</> : <><Shuffle className="mr-2 h-4 w-4" />Show Other Reviews</>;
    $[7] = loading;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== fetchRandomReviews || $[10] !== loading || $[11] !== t5) {
    t6 = <div className="flex justify-center"><Button onClick={fetchRandomReviews} isDisabled={loading} variant="outline" size="lg" className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white border-0 px-8 py-3 rounded-lg font-medium shadow-lg hover:shadow-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed">{t5}</Button></div>;
    $[9] = fetchRandomReviews;
    $[10] = loading;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t4 || $[14] !== t6) {
    t7 = <div className="space-y-8">{t4}{t6}</div>;
    $[13] = t4;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  return t7;
}
function _temp(review, index) {
  let formattedDate = "Date unavailable";
  ;
  try {
    if (review.createdAt) {
      formattedDate = toStr(review.createdAt, "MMM DD, YYYY");
    }
  } catch (t0) {
    const error = t0;
    logger.error(`Error formatting date for review ${index}:`, error, "createdAt:", review.createdAt);
    formattedDate = review.createdAt || "Date unavailable";
  }
  const colorSchemes = [{
    border: "border-l-violet-500",
    bg: "bg-gradient-to-br from-violet-50/80 via-white to-purple-50/60",
    avatar: "from-violet-500 to-purple-600",
    ring: "ring-violet-200",
    accent: "bg-violet-100 text-violet-800"
  }, {
    border: "border-l-emerald-500",
    bg: "bg-gradient-to-br from-emerald-50/80 via-white to-teal-50/60",
    avatar: "from-emerald-500 to-teal-600",
    ring: "ring-emerald-200",
    accent: "bg-emerald-100 text-emerald-800"
  }, {
    border: "border-l-rose-500",
    bg: "bg-gradient-to-br from-rose-50/80 via-white to-pink-50/60",
    avatar: "from-rose-500 to-pink-600",
    ring: "ring-rose-200",
    accent: "bg-rose-100 text-rose-800"
  }, {
    border: "border-l-amber-500",
    bg: "bg-gradient-to-br from-amber-50/80 via-white to-orange-50/60",
    avatar: "from-amber-500 to-orange-600",
    ring: "ring-amber-200",
    accent: "bg-amber-100 text-amber-800"
  }, {
    border: "border-l-cyan-500",
    bg: "bg-gradient-to-br from-cyan-50/80 via-white to-blue-50/60",
    avatar: "from-cyan-500 to-blue-600",
    ring: "ring-cyan-200",
    accent: "bg-cyan-100 text-cyan-800"
  }, {
    border: "border-l-indigo-500",
    bg: "bg-gradient-to-br from-indigo-50/80 via-white to-blue-50/60",
    avatar: "from-indigo-500 to-blue-600",
    ring: "ring-indigo-200",
    accent: "bg-indigo-100 text-indigo-800"
  }];
  const scheme = colorSchemes[index % colorSchemes.length];
  return <Card key={index} className={`p-6 hover:shadow-xl hover:scale-[1.02] transition-all duration-300 border-l-4 ${scheme.border} ${scheme.bg} backdrop-blur-sm border border-gray-100/50 shadow-lg`}><div className="flex items-center justify-between mb-4"><div className="flex items-center gap-3">{review.reviewer?.profilePhotoUrl ? <Avatar className="w-12 h-12"><AvatarImage src={review.reviewer.profilePhotoUrl} alt={review.reviewer.displayName || "Reviewer"} /><AvatarFallback>{review.reviewer?.displayName?.charAt(0).toUpperCase() || " "}</AvatarFallback></Avatar> : null}<div className={`w-12 h-12 rounded-full bg-gradient-to-br ${scheme.avatar} flex items-center justify-center text-white font-bold text-lg shadow-md ${review.reviewer?.profilePhotoUrl ? "hidden" : "flex"}`}>{(review.reviewer?.displayName || "A").charAt(0).toUpperCase()}</div><div><p className="font-bold text-gray-900 text-sm">{review.reviewer?.displayName || "Anonymous"}</p><p className="text-xs text-gray-600 font-medium">{formattedDate}</p></div></div><div className="flex items-center gap-1 bg-gradient-to-r from-yellow-100 to-amber-100 px-3 py-2 rounded-full shadow-sm border border-yellow-200">{[...Array(5)].map((_, i) => <Star key={i} className={`w-4 h-4 ${i < review.rating ? "text-yellow-500 fill-yellow-500 drop-shadow-sm" : "text-gray-300"}`} />)}<span className="text-sm font-bold text-yellow-700 ml-2">{review.rating}</span></div></div><p className="text-gray-700 mb-4 line-clamp-4 leading-relaxed font-medium">{review.review}</p>{review.summary && <div className="bg-gradient-to-r from-slate-50 to-gray-50 p-4 rounded-xl border border-slate-200 shadow-inner"><div className="flex items-center gap-2 mb-2"><div className="w-2 h-2 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full shadow-sm" /><p className="text-sm font-bold text-slate-800">AI Summary</p></div><p className="text-sm text-slate-700 leading-relaxed font-medium">{review.summary}</p></div>}</Card>;
}